import { keepPreviousData, queryOptions } from '@tanstack/react-query'
import type { z } from 'zod'
import { fetchSpaceCountsByVenueId, fetchTaggedVenues, fetchVenueTagFacetCountsByVenueId, fetchVenueTagGroups, searchVenuesByName } from './venue-tag.api'
import type { ListSpacesParams } from '~/shared/space/space-list.params'
import type { ListTaggedVenuesInput } from '~/shared/venue-tag/venue-tag.api.schema'

export const venueTagQueries = {
  list: () =>
    queryOptions({
      queryKey: ['venue-tag', 'list'] as const,
      queryFn: fetchVenueTagGroups,
      staleTime: 30 * 1000,
    }),
  taggedVenues: (input: z.input<typeof ListTaggedVenuesInput>) =>
    queryOptions({
      queryKey: ['venue-tag', 'taggedVenues', input] as const,
      queryFn: () => fetchTaggedVenues(input),
      staleTime: 30 * 1000,
    }),
  venueSearch: (query: string, excludeVenueIds: ReadonlyArray<number>, venueGroupIds: ReadonlyArray<number>) =>
    queryOptions({
      queryKey: [
        'venue-tag',
        'venueSearch',
        query,
        [...excludeVenueIds].sort((a, b) => a - b),
        [...venueGroupIds].sort((a, b) => a - b),
      ] as const,
      queryFn: () => searchVenuesByName(query, excludeVenueIds, venueGroupIds),
      enabled: query.trim().length > 0,
      staleTime: 10 * 1000,
    }),
  // venueId → space count, used by the SRP tag filter to show spaces (not
  // venues). Sorted key keeps the cache stable across reorderings.
  spaceCountsByVenue: (venueIds: ReadonlyArray<number>, activeVenueGroupId: number | null | undefined) =>
    queryOptions({
      queryKey: [
        'venue-tag',
        'spaceCountsByVenue',
        [...new Set(venueIds)].sort((a, b) => a - b),
        activeVenueGroupId ?? null,
      ] as const,
      queryFn: () => fetchSpaceCountsByVenueId(venueIds, activeVenueGroupId),
      enabled: venueIds.length > 0,
      staleTime: 60 * 1000,
    }),
  tagFacetCountsByVenue: (venueIds: ReadonlyArray<number>, params: ListSpacesParams) =>
    queryOptions({
      queryKey: [
        'venue-tag',
        'tagFacetCountsByVenue',
        [...new Set(venueIds)].sort((a, b) => a - b),
        params,
      ] as const,
      queryFn: ({ signal }) => fetchVenueTagFacetCountsByVenueId(venueIds, params, { signal }),
      enabled: venueIds.length > 0,
      placeholderData: keepPreviousData,
      staleTime: 60 * 1000,
    }),
}
